/**
 * PreviewFrameWindow is the slice of a window the preview check reads: its own origin and its
 * parent. Typed as a slice so a test hands a fake whose parent throws on `location`, as a
 * cross-origin parent does in a browser.
 */
export interface PreviewFrameWindow {
  readonly location: { readonly origin: string };
  readonly parent: { readonly location: { readonly origin: string } };
}

/**
 * isFramedBySameOrigin reports whether the embed document runs inside a frame whose parent is
 * a page of its own origin: the one place the owner-truthful preview may run, the embed
 * wizard's preview box in the console.
 *
 * The preview reads the organization and its itineraries over the visitor's own console
 * session, so a page that could set the `preview` flag on a frame it owns would read them as
 * whoever is signed in. The host refuses such a document before it is served (the embed CSP
 * route admits a preview only for a root-origin parent); this is the same rule in the document
 * itself, for any path that reaches the SPA without that route (a dev server, a host
 * misconfigured). It fails closed:
 *
 * - a top-level document (its parent is itself) is no preview, since the wizard always frames
 *   it;
 * - a parent of another origin is no preview, whether it says so by a different origin or, as
 *   a browser does for a cross-origin parent, by throwing on the read of its location.
 */
export const isFramedBySameOrigin = (win: PreviewFrameWindow): boolean => {
  if (win.parent === win) {
    return false;
  }

  try {
    return win.parent.location.origin === win.location.origin;
  } catch {
    return false;
  }
};
